'use client';
import AttributesIconSvg from '@/assets/svgs/text-editor/attributes.svg';
import { getShortcutModifier, isMac } from '@/shared/utils/window';
import { Editor } from '@tiptap/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import useTextEditorShortcuts from '../hooks/useTextEditorShortcuts';
import AttributesDropdown from './AttributesDropdown';
import KeyboardShortcutText from './KeyboardShortcutText';

interface AttributesDropdownButtonProps {
  className?: string;
  editor: Editor | null;
  isInlineToolbar?: boolean;
}

const AttributesDropdownButton = ({
  className,
  editor,
  isInlineToolbar,
}: AttributesDropdownButtonProps) => {
  const { closeFloatingItems } = useTextEditorShortcuts();
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isClickOpen, setIsClickOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleHoverEnter = () => {
    setIsHoverOpen(true);
    setIsClickOpen(false); // Close click dropdown when hover opens
  };

  const handleHoverLeave = () => {
    setIsHoverOpen(false);
  };

  const handleClickToggle = () => {
    closeFloatingItems();
    setIsClickOpen((prev) => !prev);
    setIsHoverOpen(false); // Close hover dropdown when click toggles
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isClickOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsClickOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClickOpen]);

  return (
    <div
      ref={dropdownRef}
      className={classNames(
        'dropdown',
        {
          'dropdown-top dropdown-hover': isHoverOpen && !isClickOpen,
          'dropdown-bottom dropdown-open': isClickOpen,
          'dropdown-end dropdown-left': isInlineToolbar,
        },
        className
      )}
      data-dropdown-dismiss="true">
      <div
        tabIndex={0}
        role="button"
        onMouseDown={(e) => {
          e.preventDefault(); // Prevent button from stealing focus from editor
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClickToggle();
        }}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        className={classNames('btn btn-square btn-ghost btn-sm')}>
        <AttributesIconSvg />
      </div>
      {isHoverOpen && !isClickOpen && !isInlineToolbar && (
        <div
          tabIndex={0}
          className="dropdown-content bg-black-default/80 z-[1] mb-0.5 !flex h-[26px] min-w-[168px] items-center justify-center gap-1.5 rounded-md px-[8px] text-white">
          <div className="text-[12px]">Add attributes</div>
          <div className="shortcuts flex gap-1">
            <KeyboardShortcutText
              shortcut={getShortcutModifier()}
              className="text-[16px]"
            />
            <KeyboardShortcutText
              shortcut="⇧"
              className={isMac() ? 'pb-0.5 text-[16px]' : 'text-[12px]'}
            />
            <KeyboardShortcutText shortcut="P" className="text-[12px]" />
          </div>
        </div>
      )}
      {isClickOpen && (
        <AttributesDropdown editor={editor} onClose={() => setIsClickOpen(false)} />
      )}
    </div>
  );
};

export default AttributesDropdownButton;
